"use client";

import { useMemo } from "react";
import type { SlipLadder } from "@/components/optimizer/props-slip-breakevens";
import { legMultiplier } from "@/lib/fantasy-props/rows";
import { fetchUnderdogQuote, underdogPicks, type UnderdogPrice, type UnderdogQuote } from "@/lib/fantasy-props/underdog-quote";
import { listSlipTargets, priceLadder, topPayout, type SlipLegView, type SlipPrice, type SlipTarget } from "@/lib/fantasy-props/slip";
import { americanToDecimal } from "@/utils/calculators/odds-utils";
import { useSlipQuote, type QuoteState, type SlipPricing } from "./use-slip-quote";

export type UnderdogQuoteState =
  | { readonly status: "off" }
  | { readonly status: "unreadable" }
  | QuoteState<UnderdogQuote>;

const OFF = { status: "off" } as const;
const UNREADABLE = { status: "unreadable" } as const;
const PRICING = { status: "pricing" } as const;

export function useUnderdogQuote(book: string | null, legs: readonly SlipLegView[]): UnderdogQuoteState {
  const priced = book === "underdog" && legs.length >= 2;
  const picks = useMemo(() => (priced ? underdogPicks(legs.map((leg) => leg.current?.pick ?? null)) : null), [priced, legs]);
  const quote = useSlipQuote(picks, fetchUnderdogQuote);

  if (!priced) return OFF;
  if (legs.some((leg) => leg.current === null && !leg.gone)) return PRICING;
  if (picks === null) return UNREADABLE;
  return quote;
}

function floorCents(payout: number): number {
  return Math.floor(payout * 100 + 1e-9) / 100;
}

function sameTiers(a: SlipLadder, b: SlipLadder): boolean {
  const tiers = Object.keys(a);
  return tiers.length === Object.keys(b).length && tiers.every((tier) => tier in b);
}

type QuotedSlip = { readonly list: SlipLadder; readonly quoted: SlipLadder };

function quotedSlip(target: SlipTarget, quote: UnderdogPrice, legs: number): QuotedSlip | null {
  const sized = listSlipTargets(target.platform, 0).filter((slip) => slip.legs === legs);
  if (!target.allOrNothing) {
    const flex = sized.find((slip) => !slip.allOrNothing);
    const quoted = flex === undefined ? undefined : quote.flex.find((ladder) => sameTiers(ladder, flex.base));
    if (flex !== undefined && quoted !== undefined) return { list: flex.base, quoted };
    if (legs === target.legs) return null;
  }
  const standard = sized.find((slip) => slip.allOrNothing);
  return standard === undefined ? null : { list: standard.base, quoted: { [legs]: quote.multiplier } };
}

export function underdogPricing(state: UnderdogQuoteState, target: SlipTarget | null, price: SlipPrice, legs: readonly SlipLegView[], standard: number | null): SlipPricing {
  const none: SlipPricing = { live: null, price, tiers: undefined, mismatch: false };
  if (state.status === "off" || target === null) return none;
  if (state.status === "pricing") return { ...none, live: { status: "pricing" } };
  if (state.status !== "priced") return { ...none, live: { status: "unavailable", reason: state.status } };
  const quote = state.quote;
  if ("refusal" in quote) return { ...none, live: { status: "refused", detail: quote.refusal } };
  const slip = quotedSlip(target, quote, legs.length);
  if (slip === null) return { ...none, live: { status: "unavailable", reason: "unoffered" } };
  const picked = legs.reduce((acc, leg) => {
    const odds = leg.current?.odds ?? leg.odds;
    return odds === null ? acc : acc * legMultiplier(americanToDecimal(odds), standard);
  }, price.multiplier);
  const listed = { ...price, multiplier: picked };
  const factor = topPayout(slip.quoted) / floorCents(topPayout(slip.list) * picked);
  const live = { status: "priced", factor, uncutPayout: topPayout(priceLadder(target, listed)), stale: state.stale } as const;
  if (legs.length === target.legs) {
    return { live, price: { ...price, multiplier: 1, factor: 1 }, tiers: slip.quoted, mismatch: false };
  }
  return { live, price: { ...listed, factor }, tiers: undefined, mismatch: false };
}
